/**
 * What a job costs, as QuickBooks takes it.
 *
 * The other half of `quickbooks.ts`. That file sends the customer an invoice;
 * this one sends the accounts what the job cost to do — crew payouts, material
 * orders and typed expenses — so that a job's profit in the books is its actual
 * profit rather than its revenue.
 *
 * Until 2026-08-30 none of this existed and every job looked far more
 * profitable in QuickBooks than it was. ADR 0088 records the decision and the
 * timing Jonathon chose.
 *
 * Like `quickbooks.ts`, this is mapping and nothing else: no network, no
 * tokens, no Supabase. And like it, money arrives as integer cents (rule 3) and
 * leaves as the decimal QuickBooks insists on, in one place.
 */

import { type Cents, toCents, toDecimal } from "./money.js";
import { documentTotal } from "./ladder.js";
import { type CostDocument } from "./types.js";

/** The three kinds of cost a sold job can send to the accounts. */
export type BillSourceKind = "crew_payout" | "material_order" | "job_expense";

/**
 * Which QuickBooks account each kind of cost is booked to, by the account's
 * full name (ADR 0276, amending ADR 0088).
 *
 * These are the two cost-of-goods-sold accounts Kelsey already books to by
 * hand: crew payments and landfill fees to "Sub labor & disposal", material to
 * "Supplies & materials". They are the full names QuickBooks itself returned on
 * her own purchases. Jonathon chose them on 2026-10-07; permits, equipment and
 * other job costs go with the labor.
 *
 * The full name is what the Edge Function matches, and QuickBooks writes a
 * sub-account's full name as `Parent:Child` — no space after the colon — while
 * an account's own name cannot contain a colon at all. The names this held
 * before ("Cost of goods sold: labor", "Other job costs - COGS" and the rest)
 * therefore could never exist, and all 35 cost bills Roof Board tried to send
 * before 2026-10-07 failed on them. Those were left out rather than sent,
 * because Kelsey had already entered the costs by hand.
 *
 * Matched by name rather than by id because an id is per-company: the sandbox
 * and the real company do not share one, and hard-coding either would mean the
 * thing that worked in testing is the thing that breaks live. If a name is not
 * found the cost still books, to `FALLBACK_ACCOUNT` — a cost in roughly the
 * right place beats a push that fails and a job with no cost at all.
 */
export const COST_ACCOUNTS = {
  crew_payout: "Cost of goods sold:Sub labor & disposal - COGS",
  material_order: "Cost of goods sold:Supplies & materials - COGS",
  permit: "Cost of goods sold:Sub labor & disposal - COGS",
  disposal: "Cost of goods sold:Sub labor & disposal - COGS",
  equipment: "Cost of goods sold:Sub labor & disposal - COGS",
  other: "Cost of goods sold:Sub labor & disposal - COGS",
} as const;

/** Where a cost goes when nothing more specific matches. */
export const FALLBACK_ACCOUNT = COST_ACCOUNTS.other;

/**
 * The account a typed expense belongs in, read from what it is called.
 *
 * `job_expenses` has no category column — it has a free-text `name`, a `trade`
 * and `tags`, because it was built for humans reading a job screen rather than
 * for a chart of accounts. So the name is matched against the words that
 * actually appear in this data.
 *
 * **A permit is a job cost; a licence is overhead and never a job cost.**
 * Jonathon's rule, and the reason "license" is matched and deliberately sent
 * nowhere near the permits account.
 */
export function expenseAccount(expense: {
  name?: string | null;
  trade?: string | null;
}): string {
  const text = `${expense.name ?? ""} ${expense.trade ?? ""}`.toLowerCase();
  const has = (...words: string[]) => words.some((w) => text.includes(w));

  // Checked before permits: a licence renewal is overhead, and the word sits
  // close enough to "permit" in this data to be worth ruling out first.
  if (has("license", "licence")) return FALLBACK_ACCOUNT;
  if (has("permit")) return COST_ACCOUNTS.permit;
  if (has("dump", "disposal", "trash", "haul", "debris")) {
    return COST_ACCOUNTS.disposal;
  }
  if (has("lift", "crane", "rental", "equipment", "scaffold")) {
    return COST_ACCOUNTS.equipment;
  }
  if (has("material", "shingle", "supply", "supplies")) {
    return COST_ACCOUNTS.material_order;
  }
  return FALLBACK_ACCOUNT;
}

/** The account for any cost, whichever of the three kinds it is. */
export function costAccount(input: {
  kind: BillSourceKind;
  name?: string | null;
  trade?: string | null;
}): string {
  if (input.kind === "crew_payout") return COST_ACCOUNTS.crew_payout;
  if (input.kind === "material_order") return COST_ACCOUNTS.material_order;
  return expenseAccount(input);
}

/**
 * What a crew payout bills.
 *
 * The approved figure when somebody changed it, otherwise the work order's own
 * total — which is `payoutTotal` in payout.ts, and this defers to the same
 * rule rather than restating it. A partial approval *replaces* the amount, it
 * does not adjust it.
 */
export function payoutBillAmount(order: {
  payout_amount?: number | string | null;
  items?: readonly {
    qty?: number | string | null;
    unit_price?: number | string | null;
  }[];
  tax_rate?: number | string | null;
  reconcile_amt?: number | string | null;
}): Cents {
  return order.payout_amount == null
    ? documentTotal(order as CostDocument)
    : toCents(order.payout_amount);
}

/**
 * What a material order bills: the document total, tax included.
 *
 * The tax on materials is a real cost to CopperRidge and belongs inside the
 * material cost — the company never charges the customer sales tax, it pays it
 * to the supplier. Same arithmetic the ladder uses, so the books and the job
 * screen cannot disagree.
 */
export function materialBillAmount(order: CostDocument): Cents {
  return documentTotal(order);
}

/** What a typed expense bills. */
export function expenseBillAmount(expense: {
  amount?: number | string | null;
}): Cents {
  return toCents(expense.amount);
}

/** A QuickBooks bill, as the API takes it. */
export interface QboBill {
  VendorRef: { value: string };
  Line: {
    Id?: string;
    DetailType: "AccountBasedExpenseLineDetail";
    Amount: number;
    Description?: string;
    AccountBasedExpenseLineDetail: {
      AccountRef: { value: string };
      CustomerRef?: { value: string };
      BillableStatus?: string;
    };
  }[];
  TxnDate?: string;
  DocNumber?: string;
  PrivateNote?: string;
}

/**
 * A cost, as a QuickBooks bill.
 *
 * The line carries `CustomerRef` — the customer whose job this cost belongs to
 * — because that is the whole point of sending it. Without it QuickBooks has a
 * cost and no idea which job it was for, and job profitability in the books
 * stays exactly as wrong as it is today.
 *
 * `BillableStatus` is `NotBillable`. The customer has already been invoiced the
 * contract amount; marking these billable would queue them to be charged the
 * cost on top of it.
 */
export function quickbooksBill(input: {
  vendorId: string;
  accountId: string;
  amount: Cents;
  /** The customer this job belongs to, so the cost lands against the job. */
  customerId?: string | null;
  description?: string | null;
  /** `YYYY-MM-DD` — the day the cost was incurred. */
  txnDate?: string | null;
  docNumber?: string | number | null;
}): QboBill {
  const detail: QboBill["Line"][number]["AccountBasedExpenseLineDetail"] = {
    AccountRef: { value: input.accountId },
  };
  if (input.customerId) {
    detail.CustomerRef = { value: input.customerId };
    detail.BillableStatus = "NotBillable";
  }

  const description = (input.description ?? "").trim();
  const bill: QboBill = {
    VendorRef: { value: input.vendorId },
    Line: [
      {
        DetailType: "AccountBasedExpenseLineDetail",
        Amount: toDecimal(input.amount),
        ...(description ? { Description: description.slice(0, 4000) } : {}),
        AccountBasedExpenseLineDetail: detail,
      },
    ],
  };
  if (input.txnDate) bill.TxnDate = input.txnDate;
  if (input.docNumber != null && String(input.docNumber).trim()) {
    bill.DocNumber = String(input.docNumber).trim().slice(0, 21);
  }
  if (description) bill.PrivateNote = `Roof Board ${description}`;
  return bill;
}

/** What QuickBooks holds for a bill Roof Board sent before, as a query reads it. */
export interface QboBillOnFile {
  Id: string;
  SyncToken?: string | number | null;
  Line?: unknown[] | null;
  TotalAmt?: number | null;
  Balance?: number | null;
}

/** What to do with a bill that is being sent again. */
export type BillResend =
  /** QuickBooks no longer has a live bill for it: raise a new one. */
  | { action: "create" }
  /** Change the bill QuickBooks has to what Roof Board says now. */
  | {
      action: "update";
      body: QboBill & { Id: string; SyncToken: string; sparse: true };
    }
  /** It already says this. Nothing to write. */
  | { action: "unchanged" }
  /** Leave it for a person, and say why. */
  | { action: "refuse"; reason: string };

/**
 * A payout approved again after an Undo: change the bill QuickBooks already
 * has, or leave it for a person (ADR 0179).
 *
 * Updated in place rather than voided and raised again. QuickBooks' API has no
 * void for a bill — only delete — and deleting one throws away the record that
 * a bill existed, which is worse than an edit QuickBooks keeps in its own audit
 * log. One bill per work order, in QuickBooks as it is in `quickbooks_bills`.
 *
 * The refusals are `invoiceCorrection`'s, for the same reasons. A bill somebody
 * has paid against in QuickBooks is an accounting decision, not a new total; a
 * bill with more than one line was built by hand there, and which line the
 * difference belongs on is not ours to guess. Both land on the queue row as a
 * reason and show in Tools -> Integrations.
 *
 * A bill QuickBooks no longer has — deleted there, which is what the stranded
 * notice used to tell people to do — or has voided to nothing is raised again:
 * there is no live bill left to duplicate.
 *
 * @param bill   the bill as it would be sent today (`quickbooksBill`)
 * @param amount what it is for, in cents
 * @param onFile what QuickBooks holds under the id we kept, or null if nothing
 */
export function billResend(input: {
  bill: QboBill;
  amount: Cents;
  onFile: QboBillOnFile | null;
}): BillResend {
  const { bill, amount, onFile } = input;
  if (!onFile) return { action: "create" };

  const total = toCents(onFile.TotalAmt ?? 0);
  if (total === 0 && amount !== 0) return { action: "create" };

  const balance = toCents(onFile.Balance ?? onFile.TotalAmt ?? 0);
  if (balance < total) {
    return {
      action: "refuse",
      reason:
        "This bill changed, but QuickBooks " +
        "already shows a payment against the old bill. Correct it there — " +
        "Roof Board has left it alone.",
    };
  }

  const lines = Array.isArray(onFile.Line) ? onFile.Line : [];
  if (lines.length !== 1) {
    return {
      action: "refuse",
      reason:
        "This bill changed, but it has " +
        "more than one line in QuickBooks. Correct it there — Roof Board " +
        "cannot tell which line the difference belongs on.",
    };
  }

  if (total === amount) return { action: "unchanged" };

  // QuickBooks treats a line without its existing Id as a newly added line,
  // even on a sparse bill update. Never risk doubling a cost on the bill.
  const lineId = String((lines[0] as { Id?: unknown })?.Id ?? "");
  if (!/^[1-9]\d*$/.test(lineId)) {
    return {
      action: "refuse",
      reason:
        "This bill changed, but QuickBooks did not return its line ID. " +
        "Correct it there — Roof Board has left it alone.",
    };
  }

  return {
    action: "update",
    body: {
      ...bill,
      Line: [{ ...bill.Line[0]!, Id: lineId }],
      Id: String(onFile.Id),
      SyncToken: String(onFile.SyncToken ?? "0"),
      sparse: true,
    },
  };
}

/**
 * What the line on the bill says, so somebody reading the accounts can tell
 * what they are looking at without opening Roof Board.
 */
export function billDescription(input: {
  kind: BillSourceKind;
  jobNumber?: number | string | null;
  address?: string | null;
  what?: string | null;
}): string {
  const job = input.jobNumber == null ? "" : `Job ${input.jobNumber}`;
  const kind =
    input.kind === "crew_payout"
      ? "Labor"
      : input.kind === "material_order"
        ? "Material"
        : (input.what ?? "").trim() || "Job cost";
  const where = (input.address ?? "").trim();
  return [job, kind, where].filter(Boolean).join(" · ");
}

/**
 * Every state a queued bill can be in.
 *
 * `left_out` is a cost on a sold job recorded before costs could reach
 * QuickBooks (the day the change went live, October 2026): it is not sent, so
 * nothing Kelsey entered by hand is doubled (ADR 0276). Nothing moves it back
 * to `pending`, not even a crew payout undone and approved again. Nobody has anything to do
 * about one, and it is not a failure.
 */
export const QUICKBOOKS_BILL_STATES = [
  "pending",
  "sent",
  "failed",
  "stranded",
  "left_out",
] as const;

export type QuickbooksBillState = (typeof QUICKBOOKS_BILL_STATES)[number];

/** What the Integrations screen says about a queued bill. */
export function quickbooksBillNotice(
  state: QuickbooksBillState,
  lastError?: string | null,
): string {
  const error = (lastError ?? "").trim();
  if (state === "sent") return "In QuickBooks.";
  // Said the same way for every such row, whatever its last_error carries.
  if (state === "left_out") {
    return "Left out on purpose — from before costs reached QuickBooks. Not sent.";
  }
  if (state === "stranded") {
    return "QuickBooks has this bill but the cost was undone here. Void it in QuickBooks.";
  }
  if (state === "failed") {
    return error || "QuickBooks refused it, and did not say why.";
  }
  // The failure this wording exists for: a vendor that does not exist yet. It
  // is the expected state for every crew until somebody sets them up.
  if (error) return error;
  return "Waiting to go to QuickBooks.";
}

/**
 * What the four crews ADR 0265 split were called before it, by the company
 * name each carries now. Migration 20261004120000 is the only other record of
 * these: the row keeps no old name, so this list is how a vendor made under
 * one is still found. One of them was typed with two spaces before the dash,
 * and that spelling is kept.
 */
const CREW_NAMES_BEFORE_THE_SPLIT: ReadonlyMap<string, string> = new Map([
  ["Apex Metal", "Brad Kiley - Apex Metal"],
  ["JLC Remodeling", "Jesus Merano  - JLC Remodeling"],
  ["AL Roofing and Construction", "Jesus Palma - AL Roofing and Construction"],
  [
    "JP Roofing & Renovation LLC",
    "JP Roofing & Renovation LLC - Pablo Pimentel",
  ],
]);

/**
 * The names a crew or supplier is known by in QuickBooks, best first. The
 * Edge Function uses a vendor id already stored on the row before any of
 * these; without one it looks a vendor up by each name in turn and creates
 * one under the first only when none is found.
 *
 * A crew is created under its crew name, the company (Jonathon, 2026-10-06,
 * ADR 0265: "Add it under its crew name, as the app did before"). Since
 * ADR 0265 the office edits that name ("Brad Kiley - Apex Metal" became
 * "Apex Metal"), so a vendor QuickBooks already has may carry another one.
 * Before creating, one of the four crews the split renamed is also looked
 * for under its name from before the split, in both spacings, so it is never
 * added a second time. Never under its pay-to name: that is free text another
 * crew, a person or a supplier can share, and the vendor found is remembered
 * on the crew for good, so a match there would send its bills and 1099 to
 * somebody else. A supplier is looked up and created by its name alone, as
 * before.
 *
 * QuickBooks reads a colon as a sub-vendor separator and refuses a name over
 * a hundred characters, so each is cleaned the same way the customer's are
 * (`quickbooksDisplayName`).
 */
export function quickbooksVendorNames(
  source: { name?: string | null },
  table: "crews" | "suppliers",
): string[] {
  const clean = (value: string | null | undefined) =>
    (value ?? "").trim().replace(/:/g, "-").trim().slice(0, 100);
  const name = clean(source.name);
  const candidates = [name];
  if (table === "crews") {
    const before = CREW_NAMES_BEFORE_THE_SPLIT.get(name);
    if (before) {
      candidates.push(clean(before), clean(before.replace(/\s+/g, " ")));
    }
  }
  const names: string[] = [];
  for (const candidate of candidates) {
    if (candidate && !names.includes(candidate)) names.push(candidate);
  }
  return names;
}
